import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Lock,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Palette,
  SlidersHorizontal,
  Building2,
  FileText,
  LayoutDashboard,
  Save,
  Camera,
} from 'lucide-react';
import { useAuth, hasPermission } from '@/context/AuthContext';
import { useEchoHireTheme } from '@/theme/ThemeProvider';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import { API_CONFIG } from '@/config/config.js';
import ThemeToggle from '@/components/ThemeToggle';
import PageHeader from '@/components/ux/PageHeader';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import CompanyProfileSection from '@/components/settings/CompanyProfileSection';
import EmailSetupSection from '@/components/settings/EmailSetupSection';
import EmailTemplatesSection from '@/components/settings/EmailTemplatesSection';
import SettingsOverview from '@/components/settings/SettingsOverview';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
};

function AlertBanner({ variant, children }) {
  return (
    <div
      role="alert"
      className={cn(
        'rounded-md border px-4 py-3 text-sm',
        variant === 'error' && 'border-destructive/40 bg-destructive/10 text-destructive',
        variant === 'success' && 'border-success/40 bg-success/10 text-success'
      )}
    >
      {children}
    </div>
  );
}

function PasswordField({ id, label, name, value, onChange, show, onToggleShow, disabled }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          name={name}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="h-11 pr-10"
          autoComplete={name === 'currentPassword' ? 'current-password' : 'new-password'}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-11 w-11 text-muted-foreground"
          onClick={onToggleShow}
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState(getUserFromStorage());
  const { mode, resolvedMode } = useEchoHireTheme();

  // Inline personal-profile editing (replaces the old edit dialog)
  const [inlineProfile, setInlineProfile] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    email:     user?.email     || '',
  });
  const [inlineDirty,  setInlineDirty]  = useState(false);

  // ── Profile picture ───────────────────────────────────────────────────────
  const avatarInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarSrc = user?.avatarUrl
    ? (user.avatarUrl.startsWith('http') ? user.avatarUrl : `${API_CONFIG.STATIC_BASE_URL}${user.avatarUrl}`)
    : null;

  /** Persist the new avatar on the stored user so every surface picks it up. */
  const applyAvatar = (avatarUrl) => {
    const updated = { ...user, avatarUrl };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('echohire:avatar-changed', { detail: { avatarUrl } }));
  };

  const pickAvatar = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Picture must be 2 MB or smaller.');
    setAvatarBusy(true);
    try {
      const res = await apiClient.uploadAvatar(file);
      applyAvatar(res.data.avatarUrl);
      toast.success('Profile picture updated.');
    } catch (e) {
      toast.error(e.message || 'Could not upload the picture.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await apiClient.deleteAvatar();
      applyAvatar('');
      toast.success('Profile picture removed.');
    } catch (e) {
      toast.error(e.message || 'Could not remove the picture.');
    } finally {
      setAvatarBusy(false);
    }
  };
  const [inlineSaving, setInlineSaving] = useState(false);

  const handleInlineChange = (e) => {
    setInlineProfile(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setInlineDirty(true);
  };

  const handleInlineSave = async () => {
    if (!inlineProfile.firstName.trim()) return toast.error('First name is required.');
    if (!inlineProfile.email.trim())     return toast.error('Email is required.');
    setInlineSaving(true);
    try {
      const res = await apiClient.updateProfile(inlineProfile);
      const updated = { ...user, ...res.data };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      setInlineDirty(false);
      toast.success('Profile updated.');
    } catch (e) {
      toast.error(e.message || 'Could not update your profile.');
    } finally {
      setInlineSaving(false);
    }
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [pwData, setPwData] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  const [delOpen, setDelOpen] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [delError, setDelError] = useState('');

  // ── Interview settings (admin/hr_manager only) ────────────────────────────
  const canManageSettings = hasPermission(user, 'manage_settings');
  const [passThreshold, setPassThreshold] = useState(70);
  // How long an interview link stays usable either side of its slot.
  const [graceHours, setGraceHours]   = useState(24);
  const [earlyMins,  setEarlyMins]    = useState(120);
  const [thresholdLoading, setThresholdLoading] = useState(false);
  const [thresholdSaving, setThresholdSaving] = useState(false);

  useEffect(() => {
    if (!canManageSettings) return;
    setThresholdLoading(true);
    apiClient.getInterviewSettings()
      .then(res => {
        setPassThreshold(res.data?.passThreshold ?? 70);
        setGraceHours(res.data?.linkGraceHours ?? 24);
        setEarlyMins(res.data?.linkEarlyAccessMinutes ?? 120);
      })
      .catch(() => {}) // silent — endpoint may not exist on older deploys
      .finally(() => setThresholdLoading(false));
  }, [canManageSettings]);

  const handleSaveThreshold = async () => {
    const val = Number(passThreshold);
    if (isNaN(val) || val < 0 || val > 100) return toast.error('Threshold must be between 0 and 100.');
    const grace = Number(graceHours);
    if (isNaN(grace) || grace < 0 || grace > 168) return toast.error('Late window must be between 0 and 168 hours.');
    const early = Number(earlyMins);
    if (isNaN(early) || early < 0 || early > 10080) return toast.error('Early window must be between 0 minutes and 7 days.');
    setThresholdSaving(true);
    try {
      await apiClient.updateInterviewSettings({
        passThreshold: val,
        linkGraceHours: grace,
        linkEarlyAccessMinutes: early,
      });
      toast.success('Interview settings updated.');
    } catch (err) {
      toast.error(err.message || 'Failed to update threshold.');
    } finally {
      setThresholdSaving(false);
    }
  };

  const initials =
    `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    'U';

  // ── Navigation ────────────────────────────────────────────────────────────
  // Grouped rather than a flat list: a corporate admin thinks in terms of
  // "my account" vs "our company", and the grouping makes the company-wide
  // settings visibly distinct from personal ones.
  //
  // Driven by the URL hash so a section can be linked to directly, e.g.
  // /admin/settings#company.
  // A single horizontal row above the content. Group labels are dropped —
  // they carry no weight laid out left-to-right — so the company section is
  // renamed "Company" to stay distinct from the personal "My profile".
  const allTabs = [
    { key: 'overview',   label: 'Overview',    icon: LayoutDashboard,   show: canManageSettings },
    { key: 'profile',    label: 'My profile',  icon: User,              show: true },
    { key: 'security',   label: 'Security',    icon: Lock,              show: true },
    { key: 'company',    label: 'Company',     icon: Building2,         show: canManageSettings },
    { key: 'email',      label: 'Email setup', icon: Mail,              show: canManageSettings },
    { key: 'templates',  label: 'Templates',   icon: FileText,          show: canManageSettings },
    { key: 'interview',  label: 'Interview',   icon: SlidersHorizontal, show: canManageSettings },
    { key: 'appearance', label: 'Appearance',  icon: Palette,           show: true },
  ].filter(t => t.show);

  const defaultTab = canManageSettings ? 'overview' : 'profile';

  const [activeTab, setActiveTab] = useState(() => {
    const fromHash = window.location.hash.replace('#', '');
    return fromHash || defaultTab;
  });

  // Keep the tab valid if permissions mean it is not rendered for this user.
  useEffect(() => {
    if (!allTabs.some(t => t.key === activeTab)) setActiveTab(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSettings]);

  const selectTab = (key) => {
    setActiveTab(key);
    window.history.replaceState(null, '', `#${key}`);
  };

  // ── Sidebar status ────────────────────────────────────────────────────────
  // Small live signals next to each item, so the nav doubles as a dashboard.
  const [navStatus, setNavStatus] = useState({});

  useEffect(() => {
    if (!canManageSettings) return;
    let cancelled = false;
    (async () => {
      const [cfg, tpls] = await Promise.allSettled([
        apiClient.getCompanyEmailConfig(),
        apiClient.getEmailTemplates(),
      ]);
      if (cancelled) return;
      const edited = tpls.status === 'fulfilled'
        ? (tpls.value.data || []).filter(t => !t.isDefault).length
        : 0;
      setNavStatus({
        emailStatus: cfg.status === 'fulfilled' ? cfg.value.data?.status : undefined,
        templatesEdited: edited,
      });
    })();
    return () => { cancelled = true; };
    // Re-read after a visit to either section, so the badges reflect changes
    // made there. Depending on the booleans rather than activeTab avoids
    // refetching on every unrelated tab switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSettings, activeTab === 'email', activeTab === 'templates']);

  /** Trailing indicator for a nav item — a dot for state, a count for progress. */
  const navBadge = (key) => {
    if (key === 'email') {
      const s = navStatus.emailStatus;
      if (!s || s === 'unconfigured') return null;
      const tone = s === 'verified' ? 'bg-emerald-500'
                 : s === 'failing'  ? 'bg-destructive' : 'bg-amber-500';
      const label = s === 'verified' ? 'Active' : s === 'failing' ? 'Not working' : 'Needs verifying';
      return <span className={cn('h-1.5 w-1.5 rounded-full', tone)} title={label} />;
    }
    if (key === 'templates' && navStatus.templatesEdited > 0) {
      return <span className="text-[10px] text-muted-foreground">{navStatus.templatesEdited} edited</span>;
    }
    return null;
  };

  const handlePwOpen = () => {
    setPwData({ currentPassword: '', newPassword: '', confirm: '' });
    setPwError('');
    setPwSuccess('');
    setShowPasswords({ current: false, new: false, confirm: false });
    setPwOpen(true);
  };

  const handlePwChange = (e) => setPwData({ ...pwData, [e.target.name]: e.target.value });

  const handlePwSave = async () => {
    setPwLoading(true);
    setPwError('');
    setPwSuccess('');
    if (!pwData.currentPassword || !pwData.newPassword || !pwData.confirm) {
      setPwError('All fields are required.');
      setPwLoading(false);
      return;
    }
    if (pwData.newPassword !== pwData.confirm) {
      setPwError('New passwords do not match.');
      setPwLoading(false);
      return;
    }
    try {
      await apiClient.changePassword({
        currentPassword: pwData.currentPassword,
        newPassword: pwData.newPassword,
      });
      setPwSuccess('Password changed successfully.');
      setTimeout(() => setPwOpen(false), 1000);
    } catch (e) {
      setPwError(e.message || 'Failed to change password.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleDelConfirm = async () => {
    setDelLoading(true);
    setDelError('');
    try {
      await apiClient.deleteAccount();
      logout();
      navigate('/login', { replace: true });
    } catch (e) {
      setDelError(e.message || 'Failed to delete account.');
    } finally {
      setDelLoading(false);
    }
  };

  return (
    <div className="mx-auto space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your account, your company, and how candidates experience your hiring."
      />

      {/* ── Section tabs ─────────────────────────────────────────────── */}
      <div className="border-b border-border">
        <nav
          aria-label="Settings sections"
          className="-mb-px flex gap-1 overflow-x-auto"
        >
          {allTabs.map(t => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors',
                  active
                    ? 'border-primary font-medium text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <t.icon className="h-4 w-4 shrink-0" aria-hidden />
                {t.label}
                {navBadge(t.key)}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="space-y-6">

      {activeTab === 'overview' && canManageSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Setup overview</CardTitle>
            <CardDescription>
              Where your configuration stands, and what candidates currently experience.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsOverview onNavigate={selectTab} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'appearance' && (
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Palette className="h-5 w-5 text-primary" aria-hidden />
              Appearance
            </CardTitle>
            <CardDescription>
              Light, dark, or match system — EchoHire brand teal in all modes.
            </CardDescription>
            <p className="text-xs text-muted-foreground">
              Active: {mode === 'system' ? `System (${resolvedMode})` : mode}
            </p>
          </div>
          <ThemeToggle size="small" />
        </CardHeader>
      </Card>
      )}

      {activeTab === 'company' && canManageSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" aria-hidden />
              </span>
              Company profile
            </CardTitle>
            <CardDescription>
              These details appear on your public job pages and in candidate emails.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CompanyProfileSection />
          </CardContent>
        </Card>
      )}

      {activeTab === 'email' && canManageSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-5 w-5 text-primary" aria-hidden />
              </span>
              Email setup
            </CardTitle>
            <CardDescription>
              Send candidate emails from your own mail server instead of EchoHire.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EmailSetupSection />
          </CardContent>
        </Card>
      )}

      {activeTab === 'templates' && canManageSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <FileText className="h-5 w-5 text-primary" aria-hidden />
              </span>
              Email templates
            </CardTitle>
            <CardDescription>
              Reword the emails candidates receive. Details like login credentials are added automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EmailTemplatesSection />
          </CardContent>
        </Card>
      )}

      {activeTab === 'profile' && (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="group relative shrink-0">
              <Avatar className="h-14 w-14">
                {avatarSrc && <AvatarImage src={avatarSrc} alt="Your profile picture" />}
                <AvatarFallback className="bg-primary text-lg font-bold text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarBusy}
                aria-label="Change profile picture"
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/55 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-100"
              >
                {avatarBusy
                  ? <Loader2 className="h-4 w-4 animate-spin text-white" />
                  : <Camera className="h-4 w-4 text-white" />}
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={e => { pickAvatar(e.target.files?.[0]); e.target.value = ''; }}
              />
            </div>
            <div className="min-w-0">
              <CardTitle>Profile information</CardTitle>
              <CardDescription>
                Update your details. Hover your picture to change it
                {avatarSrc ? ' — ' : '.'}
                {avatarSrc && (
                  <button type="button" onClick={removeAvatar} disabled={avatarBusy}
                    className="underline underline-offset-2 hover:text-destructive">
                    remove it
                  </button>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Edited in place rather than behind a dialog — changing your own
              name should not need a modal. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pf-first">First name</Label>
              <Input id="pf-first" name="firstName" value={inlineProfile.firstName}
                onChange={handleInlineChange} className="h-11" autoComplete="given-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pf-last">Last name</Label>
              <Input id="pf-last" name="lastName" value={inlineProfile.lastName}
                onChange={handleInlineChange} className="h-11" autoComplete="family-name" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="pf-email">Email address</Label>
              <Input id="pf-email" name="email" type="email" value={inlineProfile.email}
                onChange={handleInlineChange} className="h-11" autoComplete="email" />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
            <p className="text-[11px] text-muted-foreground">
              {inlineDirty ? 'You have unsaved changes.' : 'All changes saved.'}
            </p>
            <Button onClick={handleInlineSave} disabled={inlineSaving || !inlineDirty} className="gap-2">
              {inlineSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {activeTab === 'security' && (
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="border-success/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success/15">
                <Lock className="h-5 w-5 text-success" aria-hidden />
              </span>
              Change password
            </CardTitle>
            <CardDescription>Update your password to keep your account secure</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={handlePwOpen} className="gap-2 border-success/50 text-success hover:bg-success/10">
              <Lock className="h-4 w-4" aria-hidden />
              Change password
            </Button>
          </CardContent>
        </Card>

        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/15">
                <Trash2 className="h-5 w-5 text-destructive" aria-hidden />
              </span>
              Delete account
            </CardTitle>
            <CardDescription>Permanently delete your account and all associated data</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={() => {
                setDelError('');
                setDelOpen(true);
              }}
              className="gap-2 border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Delete account
            </Button>
          </CardContent>
        </Card>
      </div>
      )}

      {/* ── Interview Settings (admin / hr_manager only) ─────────────────── */}
      {activeTab === 'interview' && canManageSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <SlidersHorizontal className="h-5 w-5 text-primary" aria-hidden />
              </span>
              Interview Settings
            </CardTitle>
            <CardDescription>Company-wide defaults applied to all interview sessions.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-w-xs space-y-2">
              <Label htmlFor="pass-threshold">
                Pass Threshold (%)
                <span className="ml-2 text-xs text-muted-foreground">
                  Candidates scoring at or above this mark pass a round.
                </span>
              </Label>
              {thresholdLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Input
                    id="pass-threshold"
                    type="number"
                    min={0}
                    max={100}
                    value={passThreshold}
                    onChange={e => setPassThreshold(e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">out of 100</span>
                </div>
              )}
              {/* Interview link window — when a candidate can actually start */}
              <div className="mt-5 space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                <div>
                  <p className="text-sm font-semibold text-foreground">Interview link window</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    How long the interview link works either side of the scheduled time. The link used to
                    stop working the moment the slot passed, which locked out anyone running late.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="early-mins" className="text-xs">Candidate can start early by</Label>
                    <div className="flex items-center gap-2">
                      <Input id="early-mins" type="number" min={0} max={10080} className="w-24"
                        value={earlyMins} onChange={e => setEarlyMins(e.target.value)} />
                      <span className="text-sm text-muted-foreground">minutes</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">0 means only from the exact slot time.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="grace-hours" className="text-xs">Link still works after the slot for</Label>
                    <div className="flex items-center gap-2">
                      <Input id="grace-hours" type="number" min={0} max={168} className="w-24"
                        value={graceHours} onChange={e => setGraceHours(e.target.value)} />
                      <span className="text-sm text-muted-foreground">hours</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">0 expires it exactly on time, as before.</p>
                  </div>
                </div>
              </div>

              <div className="pt-1">
                <Button
                  size="sm"
                  onClick={handleSaveThreshold}
                  disabled={thresholdSaving || thresholdLoading}
                >
                  {thresholdSaving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  Save interview settings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {pwError && <AlertBanner variant="error">{pwError}</AlertBanner>}
            {pwSuccess && <AlertBanner variant="success">{pwSuccess}</AlertBanner>}
            <PasswordField
              id="currentPassword"
              label="Current password"
              name="currentPassword"
              value={pwData.currentPassword}
              onChange={handlePwChange}
              show={showPasswords.current}
              onToggleShow={() => setShowPasswords((s) => ({ ...s, current: !s.current }))}
              disabled={pwLoading}
            />
            <PasswordField
              id="newPassword"
              label="New password"
              name="newPassword"
              value={pwData.newPassword}
              onChange={handlePwChange}
              show={showPasswords.new}
              onToggleShow={() => setShowPasswords((s) => ({ ...s, new: !s.new }))}
              disabled={pwLoading}
            />
            <PasswordField
              id="confirm"
              label="Confirm new password"
              name="confirm"
              value={pwData.confirm}
              onChange={handlePwChange}
              show={showPasswords.confirm}
              onToggleShow={() => setShowPasswords((s) => ({ ...s, confirm: !s.confirm }))}
              disabled={pwLoading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={pwLoading}>
              Cancel
            </Button>
            <Button onClick={handlePwSave} disabled={pwLoading}>
              {pwLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Updating…
                </>
              ) : (
                'Change password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title="Delete account"
        description={
          delError
            ? delError
            : 'Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.'
        }
        confirmLabel="Delete account"
        cancelLabel="Cancel"
        variant="destructive"
        loading={delLoading}
        onConfirm={handleDelConfirm}
      />
    </div>
  );
};

export default SettingsPage;
